/**
 * @module SurveyPublic
 * @description Öffentliches Kundenzufriedenheits-Formular (Token-basiert, kein Login).
 * Standalone-Seite ohne Sidebar/Header mit WeShip-Branding.
 * Dynamische Fragen aus survey_fragen Tabelle: Stars (1-5), NPS (0-10), Text, Ja/Nein.
 *
 * @route /feedback/:token
 * @since v2.34.0
 * @updated v2.119.0 — Flexible Fragen-Engine
 */
import { useState, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';

const API = import.meta.env.VITE_API_URL || '';

// ─── Star Rating (1-5) ──────────────────────────────────────────

function StarRating({ value, onChange, label, description }) {
  const [hover, setHover] = useState(0);
  return (
    <div className="space-y-1.5">
      <div>
        <label className="text-sm font-semibold text-[var(--text-primary,#1A1A2E)]">{label}</label>
        {description && <p className="text-xs text-[var(--text-secondary,#4B5563)] mt-0.5">{description}</p>}
      </div>
      <div className="flex gap-1.5">
        {[1, 2, 3, 4, 5].map(star => (
          <button
            key={star}
            type="button"
            onClick={() => onChange(star)}
            onMouseEnter={() => setHover(star)}
            onMouseLeave={() => setHover(0)}
            className="text-3xl transition-all duration-150 focus:outline-none"
            style={{
              color: star <= (hover || value) ? '#F59E0B' : '#D1D5DB',
              transform: star <= (hover || value) ? 'scale(1.1)' : 'scale(1)',
            }}
          >
            ★
          </button>
        ))}
        {value > 0 && (
          <span className="text-sm text-[var(--text-tertiary,#6B7280)] self-center ml-2">{value}/5</span>
        )}
      </div>
    </div>
  );
}

// ─── NPS Scale (0-10) ───────────────────────────────────────────

function NpsScale({ value, onChange, label, description }) {
  const getColor = (n) => {
    if (n <= 6) return { bg: 'bg-red-50', border: 'border-red-200', active: 'bg-red-500 text-white border-red-500' };
    if (n <= 8) return { bg: 'bg-yellow-50', border: 'border-yellow-200', active: 'bg-yellow-500 text-white border-yellow-500' };
    return { bg: 'bg-green-50', border: 'border-green-200', active: 'bg-green-500 text-white border-green-500' };
  };

  return (
    <div className="space-y-1.5">
      <div>
        <label className="text-sm font-semibold text-[var(--text-primary,#1A1A2E)]">{label}</label>
        {description && <p className="text-xs text-[var(--text-secondary,#4B5563)] mt-0.5">{description}</p>}
      </div>
      <div className="flex gap-1">
        {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(n => {
          const colors = getColor(n);
          const isActive = value === n;
          return (
            <button
              key={n}
              type="button"
              onClick={() => onChange(n)}
              className={`w-8 h-8 rounded-lg text-xs font-semibold border transition-all ${
                isActive
                  ? colors.active
                  : `${colors.bg} ${colors.border} text-[var(--text-secondary,#4B5563)] hover:opacity-80`
              }`}
            >
              {n}
            </button>
          );
        })}
      </div>
      <div className="flex justify-between text-[10px] text-[var(--text-tertiary,#6B7280)] px-0.5">
        <span>0 — gar nicht</span>
        <span>10 — absolut</span>
      </div>
    </div>
  );
}

// ─── Yes/No Toggle ──────────────────────────────────────────────

function YesNoToggle({ value, onChange, label, description, lang }) {
  return (
    <div className="space-y-1.5">
      <div>
        <label className="text-sm font-semibold text-[var(--text-primary,#1A1A2E)]">{label}</label>
        {description && <p className="text-xs text-[var(--text-secondary,#4B5563)] mt-0.5">{description}</p>}
      </div>
      <div className="flex gap-2">
        <button
          type="button"
          onClick={() => onChange(1)}
          className={`px-6 py-2 rounded-lg text-sm font-semibold border transition-all ${
            value === 1
              ? 'bg-[#338edc] text-white border-[#338edc]'
              : 'bg-white text-[var(--text-secondary,#4B5563)] border-[var(--border-default,#E5E7EB)] hover:border-[var(--border-default,#E5E7EB)]'
          }`}
        >
          {lang === 'de' ? 'Ja' : 'Yes'}
        </button>
        <button
          type="button"
          onClick={() => onChange(0)}
          className={`px-6 py-2 rounded-lg text-sm font-semibold border transition-all ${
            value === 0
              ? 'bg-gray-600 text-white border-gray-600'
              : 'bg-white text-[var(--text-secondary,#4B5563)] border-[var(--border-default,#E5E7EB)] hover:border-[var(--border-default,#E5E7EB)]'
          }`}
        >
          {lang === 'de' ? 'Nein' : 'No'}
        </button>
      </div>
    </div>
  );
}

// ─── Text Field ─────────────────────────────────────────────────

function TextInput({ value, onChange, label, description, placeholder, optional, optionalLabel }) {
  return (
    <div className="space-y-1.5">
      <label className="text-sm font-semibold text-[var(--text-primary,#1A1A2E)]">
        {label}
        {optional && <span className="text-[var(--text-tertiary,#6B7280)] font-normal ml-1">({optionalLabel})</span>}
      </label>
      {description && <p className="text-xs text-[var(--text-secondary,#4B5563)] mt-0.5">{description}</p>}
      <textarea
        value={value}
        onChange={e => onChange(e.target.value)}
        placeholder={placeholder}
        rows={4}
        className="w-full rounded-lg border border-[var(--border-default,#E5E7EB)] px-3 py-2.5 text-sm text-[var(--text-primary,#1A1A2E)] placeholder-gray-400 focus:border-[#338edc] focus:ring-1 focus:ring-[#338edc] outline-none resize-none"
      />
    </div>
  );
}

// ─── Status Pages ───────────────────────────────────────────────

function StatusCard({ icon, title, subtitle }) {
  return (
    <div className="text-center py-8">
      <div className="text-5xl mb-4">{icon}</div>
      <h2 className="text-xl font-bold text-[var(--text-primary,#1A1A2E)] mb-2">{title}</h2>
      <p className="text-sm text-[var(--text-secondary,#4B5563)]">{subtitle}</p>
    </div>
  );
}

// ─── Main Component ─────────────────────────────────────────────

export default function SurveyPublic() {
  const { token } = useParams();
  const [searchParams] = useSearchParams();
  const { t, locale } = useLanguage();

  // Language: URL param > browser locale > default de
  const lang = searchParams.get('lang') || (locale === 'en' ? 'en' : 'de');

  const isPreview = token === 'preview';

  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [answers, setAnswers] = useState({}); // { [frage_id]: value }
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  // Load survey data + questions
  useEffect(() => {
    const url = isPreview
      ? `${API}/api/public/survey/preview`
      : `${API}/api/public/survey/${token}`;
    fetch(url)
      .then(r => {
        if (!r.ok) throw new Error('not_found');
        return r.json();
      })
      .then(d => {
        setData(d);
        setLoading(false);
      })
      .catch(() => {
        setError('not_found');
        setLoading(false);
      });
  }, [token, isPreview]);

  // Check if all required questions are answered
  const allRequiredAnswered = () => {
    if (!data?.fragen) return false;
    return data.fragen.filter(f => f.pflicht).every(f => {
      const val = answers[f.id];
      if (f.typ === 'text') return val && val.trim().length > 0;
      return val != null;
    });
  };

  const hasAnyAnswer = () => Object.keys(answers).length > 0;

  // Build antworten array for submission
  const buildAntworten = () => {
    return Object.entries(answers).map(([frageId, val]) => {
      const frage = data.fragen.find(f => f.id === parseInt(frageId));
      if (frage?.typ === 'text') return { frage_id: parseInt(frageId), text: val };
      return { frage_id: parseInt(frageId), wert: val };
    });
  };

  // Submit
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isPreview || !allRequiredAnswered()) return;

    setSubmitting(true);
    try {
      const res = await fetch(`${API}/api/public/survey/${token}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ antworten: buildAntworten() }),
      });
      if (res.ok) {
        setSubmitted(true);
      } else {
        const d = await res.json().catch(() => ({}));
        if (res.status === 409) setData({ already_completed: true });
        else if (res.status === 410) setData({ closed: true });
        else setError(d.error || 'error');
      }
    } catch {
      setError('network');
    } finally {
      setSubmitting(false);
    }
  };

  // Get label based on language
  const lbl = (f, field) => lang === 'en' ? (f[`${field}_en`] || f[`${field}_de`]) : f[`${field}_de`];

  // Render content
  let content;

  if (loading) {
    content = (
      <div className="flex justify-center py-16">
        <div className="animate-spin h-8 w-8 border-3 border-[#338edc] border-t-transparent rounded-full" />
      </div>
    );
  } else if (error === 'not_found') {
    content = <StatusCard icon="🔗" title={t('survey.notFound')} subtitle={t('survey.notFoundDesc')} />;
  } else if (submitted) {
    content = <StatusCard icon="🎉" title={t('survey.thankYou')} subtitle={t('survey.thankYouDesc')} />;
  } else if (data?.already_completed) {
    content = <StatusCard icon="✅" title={t('survey.alreadyCompleted')} subtitle={t('survey.alreadyCompletedDesc')} />;
  } else if (data?.closed) {
    content = <StatusCard icon="⏰" title={t('survey.closed')} subtitle={t('survey.closedDesc')} />;
  } else if (data?.ok && data?.fragen) {
    const ready = allRequiredAnswered();

    content = (
      <form onSubmit={handleSubmit}>
        {/* Preview Banner */}
        {isPreview && (
          <div className="mb-4 px-3 py-2.5 rounded-lg bg-yellow-50 border border-yellow-200 text-xs text-yellow-800 font-medium">
            {t('survey.preview.hint')}
          </div>
        )}

        {/* Header */}
        <div className="mb-6">
          <h1 className="text-xl font-bold text-[var(--text-primary,#1A1A2E)]">
            {data.titel || t('survey.title')}
          </h1>
          <p className="text-sm text-[#338edc] font-medium mt-1">{data.kunde}</p>
          <p className="text-sm text-[var(--text-secondary,#4B5563)] mt-3 leading-relaxed">
            {t('survey.description')}
          </p>
        </div>

        <div className="border-t border-[var(--border-default,#E5E7EB)] my-6" />

        {/* Dynamic Questions */}
        <div className="space-y-6">
          {data.fragen.map(frage => {
            const label = lbl(frage, 'label');
            const beschreibung = lbl(frage, 'beschreibung');

            switch (frage.typ) {
              case 'stars':
                return (
                  <StarRating
                    key={frage.id}
                    value={answers[frage.id] || 0}
                    onChange={v => setAnswers(prev => ({ ...prev, [frage.id]: v }))}
                    label={label}
                    description={beschreibung}
                  />
                );
              case 'nps':
                return (
                  <NpsScale
                    key={frage.id}
                    value={answers[frage.id] ?? null}
                    onChange={v => setAnswers(prev => ({ ...prev, [frage.id]: v }))}
                    label={label}
                    description={beschreibung}
                  />
                );
              case 'yesno':
                return (
                  <YesNoToggle
                    key={frage.id}
                    value={answers[frage.id] ?? null}
                    onChange={v => setAnswers(prev => ({ ...prev, [frage.id]: v }))}
                    label={label}
                    description={beschreibung}
                    lang={lang}
                  />
                );
              case 'text':
                return (
                  <TextInput
                    key={frage.id}
                    value={answers[frage.id] || ''}
                    onChange={v => setAnswers(prev => ({ ...prev, [frage.id]: v }))}
                    label={label}
                    description={frage.pflicht ? null : beschreibung}
                    placeholder={beschreibung || ''}
                    optional={!frage.pflicht}
                    optionalLabel={t('survey.optional')}
                  />
                );
              default:
                return null;
            }
          })}
        </div>

        <div className="border-t border-[var(--border-default,#E5E7EB)] my-6" />

        {/* Submit */}
        <button
          type="submit"
          disabled={isPreview || !ready || submitting}
          className={`w-full py-3 rounded-lg text-sm font-semibold transition-all ${
            !isPreview && ready && !submitting
              ? 'bg-[#338edc] hover:bg-[#2b7bc4] text-white'
              : 'bg-gray-100 text-[var(--text-tertiary,#6B7280)] cursor-not-allowed'
          }`}
        >
          {submitting ? (
            <span className="flex items-center justify-center gap-2">
              <span className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" />
              {t('survey.submitting')}
            </span>
          ) : isPreview ? (
            t('survey.preview.hint')
          ) : (
            t('survey.submit')
          )}
        </button>

        {!isPreview && !ready && hasAnyAnswer() && (
          <p className="text-xs text-[var(--text-tertiary,#6B7280)] text-center mt-2">
            {t('survey.rateAll')}
          </p>
        )}
      </form>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex items-start justify-center px-4 py-8 sm:py-16">
      <div className="w-full max-w-lg">
        <div className="bg-white rounded-2xl border border-[var(--border-default,#E5E7EB)] overflow-hidden">
          <div className="px-6 py-4 border-b border-[var(--border-table,#F3F4F6)] flex items-center gap-3">
            <img src="/weship-logo.png" alt="WeShip" className="h-7" />
            <span className="text-[var(--text-tertiary,#6B7280)] text-xs ml-1">Feedback</span>
          </div>
          <div className="px-6 py-6">
            {content}
          </div>
        </div>
        <p className="text-center text-xs text-[var(--text-tertiary,#6B7280)] mt-4">
          WeShip Performance v2
        </p>
      </div>
    </div>
  );
}
